/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { FC, useContext, useEffect } from 'react';
import { useTheme } from 'emotion-theming';
import { CheckCircle } from 'phosphor-react';
import { Page, Text } from '../..';
import { BillingStatusRes, Copy, ROUTES, ThemeProps } from '../../../types';
import { isAzoomee, prepayPlan, textReplacer } from '../../../utils';
import { appStore, defaultState } from '../../../state';
import avail_amazon from '../../../assets/images/avail_amazon.png';
import avail_apple from '../../../assets/images/avail_apple.png';
import avail_google from '../../../assets/images/avail_google.png';
import { Link } from 'react-router-dom';
import { matchedPlan } from '../../../utils';
import { useGetBillingStatus } from '../../../hooks';

interface PaymentSuccessProps extends Copy {
  /**
   * The payment details from the payment provider. Passed in
   * via ROUTE PROPS
   */
  // details: any;
  /**
   * The type of payment that succeeded. Passed in voa ROUTE PROPS
   */
  // type: string;
}

const PaymentSuccess: FC<PaymentSuccessProps> = ({
  fallbackCopy,
  localeCopy,
}) => {
  const theme: ThemeProps = useTheme();
  /**
   * This component receives subscription details in one of 2 ways - via the response
   * of `postBtPaymentData` (Braintree) or via url params (Vodafone). We will handle
   * both cases here.
   */
  const { dispatch, state } = useContext(appStore);
  const { plans, user, billingStatus } = state;

  const billingStatusQuery = useGetBillingStatus({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
  });

  const getActivePremiumPurchases = (billingStatus: BillingStatusRes) => {
    return billingStatus.purchases.find((purchase: any) => purchase.cancelled === false && purchase.product.id !== 0);
  }

  const subscriptionInfoData = ({ billingStatus, localeCopy, fallbackCopy }:
    {
      billingStatus: BillingStatusRes;
      localeCopy: any;
      fallbackCopy: any;
    }) => {
    const foundActivePremiumPurchases = getActivePremiumPurchases(billingStatus);

    const pickedPlan = matchedPlan(
      foundActivePremiumPurchases?.product.id,
      plans
    );

    const userPlan = pickedPlan ? pickedPlan : (!pickedPlan && foundActivePremiumPurchases) ? prepayPlan(foundActivePremiumPurchases) : undefined;

    /* AZ-3692 - Hide subscription end date until proper solution implemented */
    // const getNextBillingDate = () => {
    //   if (!foundActivePremiumPurchases) {
    //     return '';
    //   } else {
    //     return new Date(foundActivePremiumPurchases.nextBillDate)
    //       .toLocaleDateString()
    //       .replace(/\//g, '-')
    //   }
    // }

    if (userPlan) {
      const purchase = getActivePremiumPurchases(billingStatus);
      const isPrepayVoucherUser = purchase?.paymentProvider === 'PRE_PAY_VOUCHER';

      if (purchase && isPrepayVoucherUser) {
        return [
          {
            title: localeCopy?.details?.username || fallbackCopy.details.username,
            value: user.emailAddress,
          }
          /* AZ-3692 - Hide subscription end date + selected plan until web billing rework, remove parent pin field as field always blank */
          // {
          //   title: localeCopy?.details?.plan || fallbackCopy.details.plan,
          //   value: localeCopy?.plans['prepay']?.title ||
          //     fallbackCopy.plans['prepay'].title,
          // },
          // {
          //   title: localeCopy?.details?.prePayPlanEnd || fallbackCopy.details.prePayPlanEnd,
          //   value: new Date(purchase.nextBillDate).toLocaleDateString()
          //     .replace(/\//g, '-'),
          // },
          // {
          //   title: localeCopy?.details?.parentPin || fallbackCopy.details.parentPin,
          //   value: user.pinNumber,
          // }
        ]
      }

      const paymentMethod = showPaymentMethod(billingStatus, localeCopy, fallbackCopy);
      if (paymentMethod) {
        return [
          {
            title: localeCopy?.details?.username || fallbackCopy.details.username,
            value: user.emailAddress,
          }
          /* AZ-3692 - Hide subscription end date + selected plan until web billing rework, remove parent pin field as field always blank */
          // {
          //   title: localeCopy?.details?.plan || fallbackCopy.details.plan,
          //   value: localeCopy?.plans[userPlan.billingCycle.toLowerCase()]?.title ||
          //     fallbackCopy.plans[userPlan.billingCycle.toLowerCase()].title,
          // },
          // {
          //   title: localeCopy?.details?.nextPaymentDate || fallbackCopy.details.nextPaymentDate,
          //   value: getNextBillingDate(),
          // },
          // paymentMethod,
          // {
          //   title: localeCopy?.details?.parentPin || fallbackCopy.details.parentPin,
          //   value: user.pinNumber,
          // }
        ]
      }
    }
    return [];
  }

  const showPaymentMethod = (billingStatus: BillingStatusRes, localeCopy: any, fallbackCopy: any) => {
    if (billingStatus.paymentCardNumber) {
      return (
        {
          title: localeCopy?.details?.paymentMethod || fallbackCopy.details.paymentMethod,
          value: textReplacer(localeCopy?.details?.cardNoPlaceholder || fallbackCopy.details.cardNoPlaceholder,
            {
              '{LAST_FOUR_DIGITS}': billingStatus.paymentCardNumber,
            }),
        })
    } else {
      let paymentProvider = '';
      switch (getActivePremiumPurchases(billingStatus)?.paymentProvider) {
        case 'BRAINTREE': {
          paymentProvider = (localeCopy?.details?.paypal || fallbackCopy.details.paypal);
          break;
        }
        case 'VODAFONEPAY': {
          paymentProvider = (localeCopy?.details?.vodafonePay || fallbackCopy.details.vodafonePay);
          break;
        }
        default: {
          return undefined;
        }
      }
      return (
        {
          title: localeCopy?.details?.paymentMethod || fallbackCopy.details.paymentMethod,
          value: paymentProvider,
        }
      )
    }
  }

  useEffect(() => {
    /**
     * Remove form, messages, plans and user data from state on component unmount
     */
    billingStatusQuery.refetch();
    return () => {
      const { form, user } = defaultState;
      dispatch({
        type: 'UPDATE_USER',
        payload: { user },
      });
      dispatch({
        type: 'UPDATE_FORM_DATA',
        payload: { form },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page isReady={!billingStatusQuery.isLoading} title={localeCopy?.title || fallbackCopy.title}>
      <CheckCircle color={theme.colors.success} size={140} />
      {<Text
        color='secondary'
        css={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: 16,
          marginTop: 20,
          width: '100%',
          fontSize: '14px',
          fontWeight: '600',
        }}>
        {localeCopy?.details?.header || fallbackCopy.details.header}
      </Text>}
      {subscriptionInfoData({
        billingStatus,
        localeCopy,
        fallbackCopy,
      })
        .filter((data) => !!data)
        .map((data: any, index: number) => (
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              marginBottom: 16,
              width: '100%',
            }}
            key={index}>
            <Text>{data.title}</Text>
            <Text color='primary'>{data.value}</Text>
          </div>
        ))}
      <Text align='center'
        css={{ fontSize: 18, marginTop: 30 }}
        withMarkdown={true}
        weight='bold'>
        {textReplacer(
          localeCopy?.details?.download || fallbackCopy.details.download,
          {
            '{EMAIL_ADDRESS}': user.emailAddress,
          })}
      </Text>
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          marginTop: 20,
          div: {
            flex: 1,
            marginLeft: 5,
            marginRight: 5,
            '&:first-of-type': {
              marginLeft: 0,
            },
            '&:last-of-type': {
              marginRight: 0,
            },
          },
          img: {
            height: 'auto',
            maxHeight: 'auto',
            width: '100%',
            [theme.mq.desktop]: {
              maxHeight: 50,
              width: 'auto',
            },
          },
        }}>
        <div>
          <a
            href={
              isAzoomee
                ? localeCopy?.appLinks?.azoomee?.apple ||
                fallbackCopy.appLinks.azoomee.apple
                : localeCopy?.appLinks?.daVinciKids?.apple ||
                fallbackCopy.appLinks.daVinciKids.apple
            }
            rel='noreferrer'
            target='_blank'>
            <img src={avail_apple} alt='Available on Amazon.' />
          </a>
        </div>
        <div>
          <a
            href={
              isAzoomee
                ? localeCopy?.appLinks?.azoomee?.google ||
                fallbackCopy.appLinks.azoomee.google
                : localeCopy?.appLinks?.daVinciKids?.google ||
                fallbackCopy.appLinks.daVinciKids.google
            }
            rel='noreferrer'
            target='_blank'>
            <img src={avail_google} alt='Available on Apple.' />
          </a>
        </div>
        {isAzoomee && (
          <div>
            <a
              href={
                localeCopy?.appLinks?.azoomee?.amazon ||
                fallbackCopy.appLinks.azoomee.amazon
              }
              rel='noreferrer'
              target='_blank'>
              <img src={avail_amazon} alt='Available on Google.' />
            </a>
          </div>
        )}
      </div>
      <Text
        align='center'
        css={{ fontSize: 18, marginTop: 60 }}
        withMarkdown={true}
        weight='bold'>
        {localeCopy?.didYouKnow?.title || fallbackCopy.didYouKnow.title}
      </Text>
      <Text
        align='center'
        css={{ fontSize: 16, marginTop: 8 }}
        customRenderer={{
          link: ({ href, children }: any) => {
            return <Link to={href}>{children}</Link>;
          },
        }}
        withMarkdown={true}>
        {textReplacer(
          localeCopy?.didYouKnow?.text || fallbackCopy.didYouKnow.text,
          {
            '§§§': ROUTES.DASHBOARD,
          }
        )}
      </Text>
    </Page>
  );
};

export default PaymentSuccess;
